/**
 * Adverse Event / Product Complaint lifecycle service.
 *
 * Backend routes (under `programs/{programId}`):
 *   GET    patients/{patientId}/adverse-events
 *   GET    cycles/{cycleId}/adverse-events
 *   POST   patients/{patientId}/adverse-events
 *   GET    adverse-events/{id}
 *   POST   adverse-events/{id}/save-draft
 *   POST   adverse-events/{id}/submit
 *   POST   adverse-events/{id}/lcn      (PV only)
 *   GET    adverse-events/{id}/timeline
 *
 * Section 21 hard-stop #5 — never embed FormId/FormVersion in this client.
 */

import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type {
  AdverseEventDto,
  AdverseEventDetailDto,
  AdverseEventTimelineEntryDto,
  AdverseEventWorkflowStatus,
  AddAdverseEventLcnRequest,
  AdverseEventFollowUpRequest,
  AdverseEventManualEscalationRequest,
  CreateAdverseEventRequest,
  SaveAdverseEventDraftRequest,
  SubmitAdverseEventRequest,
} from '../types/adverseEvent';
import type { ProgramTaskItem } from '../types/smartTasks';
import type { AeEscalationHistoryDto } from '../types/pharmacovigilance';

export interface AeDocumentUploadResult {
  s3Key: string;
  signedUrl: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

const BASE = (programId: string) => `/programs/${programId}`;

function unwrap<T>(res: { data: ApiResponse<T> }): T {
  if (!res.data?.success) {
    const err = res.data?.errors?.[0] || res.data?.message || 'Request failed';
    throw new Error(err);
  }
  return res.data.data as T;
}

export const adverseEventService = {
  async listByPatient(programId: string, patientId: string): Promise<AdverseEventDto[]> {
    const res = await apiClient.get<ApiResponse<AdverseEventDto[]>>(
      `${BASE(programId)}/patients/${patientId}/adverse-events`,
    );
    return unwrap(res) ?? [];
  },

  async getWorkflowStatus(programId: string): Promise<AdverseEventWorkflowStatus> {
    const res = await apiClient.get<ApiResponse<AdverseEventWorkflowStatus>>(
      `${BASE(programId)}/adverse-events/workflow-status`,
    );
    return unwrap(res);
  },

  async listByCycle(programId: string, cycleId: string): Promise<AdverseEventDto[]> {
    const res = await apiClient.get<ApiResponse<AdverseEventDto[]>>(
      `${BASE(programId)}/cycles/${cycleId}/adverse-events`,
    );
    return unwrap(res) ?? [];
  },

  async create(
    programId: string,
    patientId: string,
    payload: CreateAdverseEventRequest,
  ): Promise<AdverseEventDto> {
    const res = await apiClient.post<ApiResponse<AdverseEventDto>>(
      `${BASE(programId)}/patients/${patientId}/adverse-events`,
      payload,
    );
    return unwrap(res);
  },

  async getById(programId: string, adverseEventId: string): Promise<AdverseEventDetailDto> {
    const res = await apiClient.get<ApiResponse<AdverseEventDetailDto>>(
      `${BASE(programId)}/adverse-events/${adverseEventId}`,
    );
    return unwrap(res);
  },

  async saveDraft(
    programId: string,
    adverseEventId: string,
    payload: SaveAdverseEventDraftRequest,
  ): Promise<AdverseEventDetailDto> {
    const res = await apiClient.post<ApiResponse<AdverseEventDetailDto>>(
      `${BASE(programId)}/adverse-events/${adverseEventId}/save-draft`,
      payload,
    );
    return unwrap(res);
  },

  async submit(
    programId: string,
    adverseEventId: string,
    payload: SubmitAdverseEventRequest,
  ): Promise<AdverseEventDetailDto> {
    const res = await apiClient.post<ApiResponse<AdverseEventDetailDto>>(
      `${BASE(programId)}/adverse-events/${adverseEventId}/submit`,
      payload,
    );
    return unwrap(res);
  },

  async addLcn(
    programId: string,
    adverseEventId: string,
    payload: AddAdverseEventLcnRequest,
  ): Promise<AdverseEventDetailDto> {
    const res = await apiClient.post<ApiResponse<AdverseEventDetailDto>>(
      `${BASE(programId)}/adverse-events/${adverseEventId}/lcn`,
      payload,
    );
    return unwrap(res);
  },

  async getTimeline(
    programId: string,
    adverseEventId: string,
  ): Promise<AdverseEventTimelineEntryDto[]> {
    const res = await apiClient.get<ApiResponse<AdverseEventTimelineEntryDto[]>>(
      `${BASE(programId)}/adverse-events/${adverseEventId}/timeline`,
    );
    return unwrap(res) ?? [];
  },

  async createFollowUp(
    programId: string,
    adverseEventId: string,
    payload: AdverseEventFollowUpRequest,
  ): Promise<ProgramTaskItem> {
    const res = await apiClient.post<ApiResponse<ProgramTaskItem>>(
      `${BASE(programId)}/adverse-events/${adverseEventId}/follow-up`,
      payload,
    );
    return unwrap(res);
  },

  async manualEscalate(
    programId: string,
    adverseEventId: string,
    payload: AdverseEventManualEscalationRequest,
  ): Promise<AeEscalationHistoryDto> {
    const res = await apiClient.post<ApiResponse<AeEscalationHistoryDto>>(
      `${BASE(programId)}/adverse-events/${adverseEventId}/manual-escalation`,
      payload,
    );
    return unwrap(res);
  },

  async uploadDocument(programId: string, file: File): Promise<AeDocumentUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post<ApiResponse<AeDocumentUploadResult>>(
      `${BASE(programId)}/adverse-events/documents/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return unwrap(res);
  },
};
